import { useState } from "react";
import { Autocomplete, Grid, TextField } from "@mui/material";
import { AddressType } from "@/types/globals";
import { DEFAULT_COUNTRY } from "@/utils/constants";
import { REGIONS } from "@/utils/UK";

export type AddressField = "street" | "city" | "state" | "zip";

interface AddressFieldsProps {
  address: AddressType;
  onFieldChange: (key: AddressField, value: string) => void;
}

const AddressFields: React.FC<AddressFieldsProps> = ({ address, onFieldChange }) => {
  const [touched, setTouched] = useState<Record<AddressField, boolean>>({
    street: false,
    city: false,
    state: false,
    zip: false,
  });
  const markTouched = (field: AddressField) => setTouched((prev) => ({ ...prev, [field]: true }));
  const showError = (field: AddressField) => touched[field] && !address?.[field]?.trim();

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      size={12}>
      <Grid size={12}>
        <TextField
          required
          name="street-address"
          label="Street Address"
          fullWidth
          value={address?.street ?? ""}
          error={showError("street")}
          helperText={showError("street") ? "Street is required" : ""}
          onChange={(e) => onFieldChange("street", e.target.value)}
          onBlur={() => markTouched("street")}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <TextField
          fullWidth
          label="Country"
          disabled
          value={DEFAULT_COUNTRY}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <Autocomplete
          options={REGIONS}
          autoHighlight
          value={address?.state || null}
          onChange={(_, newValue: string | null) => {
            onFieldChange("state", newValue ?? "");
          }}
          onBlur={() => markTouched("state")}
          getOptionLabel={(option) => option}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="State / Province"
              error={showError("state")}
              helperText={showError("state") ? "State / Province is required" : ""}
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  autoComplete: "off",
                }
              }}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <TextField
          value={address?.city ?? ""}
          fullWidth
          label="City"
          required
          onChange={(e) => onFieldChange("city", e.target.value)}
          onBlur={() => markTouched("city")}
          error={showError("city")}
          helperText={showError("city") ? "City is required" : ""}
          slotProps={{
            htmlInput: {
              autoComplete: "off",
            }
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <TextField
          required
          name="zip"
          value={address?.zip ?? ""}
          label="Zip / Postal code"
          fullWidth
          autoComplete="postal-code"
          onChange={(e) => onFieldChange("zip", e.target.value)}
          onBlur={() => markTouched("zip")}
          error={showError("zip")}
          helperText={showError("zip") ? "Zip / Postal code is required" : ""}
        />
      </Grid>
    </Grid>
  );
};

export default AddressFields;
